/**
 * Directed rounding modes we expose on {@link numberFormatter}. These string
 * values match `Intl.NumberFormat` / `Number.prototype.toLocaleString` options
 * (`roundingMode` in the Intl API).
 *
 * `numberPrecision` is implemented as **fraction digits** (`maximumFractionDigits`, and
 * when a mode is set, `minimumFractionDigits` too so the result is quantized to a
 * fixed number of decimals — similar to `toFixed(numberPrecision)`, but the *direction*
 * is controlled here).
 *
 * Quick intuition (examples format to **2** fraction digits):
 *
 * - `trunc` (toward **0**): chop extra precision — `1.239 -> "1.23"`, `-1.239 -> "-1.23"`.
 * - `floor` (toward **-∞**): **never increases** the numeric value — `1.239 -> "1.23"`, `-1.231 -> "-1.24"`.
 * - `ceil` (toward **+∞**): **never decreases** the numeric value — `1.231 -> "1.24"`, `-1.231 -> "-1.23"`.
 *
 * For **day counts from a fractional day value**, `floor` / `ceil` on `numberPrecision: 0`
 * often match “full days elapsed so far” vs “include the current partial day as a day”.
 */
export type NumberRoundingMode = 'trunc' | 'floor' | 'ceil'

/** `Intl` options plus `roundingMode` (typed locally for older TS `lib` targets). */
type RoundedNumberFormatOptions = Intl.NumberFormatOptions & {
  roundingMode?: NumberRoundingMode
  useGrouping?: boolean
}

export type NumberFormatProps = {
  numberPrecision?: number // Number of decimal places
  numberLocale?: string // Locale string (e.g., 'en-US')
  numberFormat?: 'standard' | 'raw' | 'abbreviated'
  /**
   * Optional number unit. When `'percentage'`, the value is formatted as a
   * number plus a trailing `%`.
   * Defaults to `'number'`. `'decimal'` and `'percent'` remain accepted because
   * persisted legacy `style` values normalize onto this canonical key.
   */
  numberUnit?: 'number' | 'percentage' | 'decimal' | 'percent'
  /**
   * Display when the value is `null` / `undefined`, non-numeric, or `NaN`.
   * Defaults to `'-'`.
   */
  numberNullDisplay?: string
  /**
   * Optional directed rounding when reducing to `numberPrecision` fraction digits.
   *
   * Omit this to keep the default engine behavior for digit reduction (Intl’s
   * default tie-breaking is `halfExpand`). When set, we pass `roundingMode`
   * through to `toLocaleString` / `Intl.NumberFormat` and pin fraction length
   * with `minimumFractionDigits` + `maximumFractionDigits` both set to
   * `numberPrecision` (so values are padded with trailing zeros to exactly that
   * many decimals).
   */
  numberRoundingMode?: NumberRoundingMode
}

/**
 * Parse mixed numeric-like values into a finite JS number for formatting.
 *
 * Behavior:
 * - Accepts numbers, bigints, and numeric strings.
 * - Strips trailing percent signs and surrounding spaces.
 * - Converts simple rational strings like `a/b` to decimals and marks them with
 *   `isRationalSlash: true` so percent formatting can treat them as ratios.
 * - Returns `null` for invalid/non-finite inputs.
 *
 * Regex quick reference:
 * - `/[\u2212\u2013\u2014]/g` normalizes unicode minus/dashes (`−`, `–`, `—`) to `-`.
 * - `/[\s\u00a0]*[%％][\s\u00a0]*$/u` strips a trailing `%`/`％` plus surrounding
 *   whitespace (including NBSP).
 * - `/^-?\d[\d,]*\s*\/\s*-?\d[\d,]*$/` matches rational strings like `27/100`.
 */
type ParsedFiniteNumber = {
  value: number
  isRationalSlash: boolean
}

const parseFiniteNumber = (value: unknown): ParsedFiniteNumber | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { value, isRationalSlash: false } : null
  }

  if (typeof value === 'bigint') {
    try {
      const n = Number(value)
      return Number.isFinite(n) ? { value: n, isRationalSlash: false } : null
    } catch {
      return null
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/[\u2212\u2013\u2014]/g, '-')
    if (!trimmed) return null

    const withoutPercent = trimmed
      .replace(/[\s\u00a0]*[%％][\s\u00a0]*$/u, '')
      .trim()

    const rationalMatch = /^-?\d[\d,]*\s*\/\s*-?\d[\d,]*$/.exec(withoutPercent)
    if (rationalMatch) {
      const [rawNum, rawDen] = withoutPercent.split('/').map(s => s.trim())
      const num = Number(rawNum.replace(/,/g, ''))
      const den = Number(rawDen.replace(/,/g, ''))
      if (Number.isFinite(num) && Number.isFinite(den) && den !== 0) {
        return { value: num / den, isRationalSlash: true }
      }
    }

    const normalized = withoutPercent.replace(/[\s\u00a0]/g, '')

    const lastComma = normalized.lastIndexOf(',')
    const lastDot = normalized.lastIndexOf('.')

    let candidate = normalized

    if (lastComma >= 0 && lastDot >= 0) {
      candidate =
        lastComma > lastDot
          ? normalized.replace(/\./g, '').replace(',', '.')
          : normalized.replace(/,/g, '')
    } else if (lastComma >= 0 && lastDot < 0) {
      const commaCount = (normalized.match(/,/g) ?? []).length
      candidate =
        commaCount === 1
          ? normalized.replace(',', '.')
          : normalized.replace(/,/g, '')
    } else {
      candidate = normalized.replace(/,/g, '')
    }

    const n = Number(candidate)
    return Number.isFinite(n) ? { value: n, isRationalSlash: false } : null
  }

  return null
}

const formatWithLocale = (
  value: number,
  locale: string,
  intlOptions: RoundedNumberFormatOptions,
): string => {
  try {
    return value.toLocaleString(locale, intlOptions as Intl.NumberFormatOptions)
  } catch {
    const { roundingMode: _roundingMode, ...rest } = intlOptions
    return value.toLocaleString(locale, rest)
  }
}

const formatDecimalDigits = (
  value: number,
  {
    precision,
    locale,
    numberFormat,
    roundingMode,
  }: {
    precision: number
    locale: string
    numberFormat: 'standard' | 'raw' | 'abbreviated' | undefined
    roundingMode: NumberRoundingMode | undefined
  },
): string => {
  const useGrouping = numberFormat !== 'raw'
  const numberFormatOptions: RoundedNumberFormatOptions = roundingMode
    ? {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
        roundingMode,
        useGrouping,
      }
    : {
        maximumFractionDigits: precision,
        useGrouping,
      }

  const absValue = Math.abs(value)

  if (numberFormat === 'abbreviated') {
    let shorthandValue
    if (absValue >= 1_000_000_000_000) {
      shorthandValue =
        formatWithLocale(
          absValue / 1_000_000_000_000,
          locale,
          numberFormatOptions,
        ) + 'T'
    } else if (absValue >= 1_000_000_000) {
      shorthandValue =
        formatWithLocale(
          absValue / 1_000_000_000,
          locale,
          numberFormatOptions,
        ) + 'B'
    } else if (absValue >= 1_000_000) {
      shorthandValue =
        formatWithLocale(absValue / 1_000_000, locale, numberFormatOptions) +
        'M'
    } else if (absValue >= 1_000) {
      shorthandValue =
        formatWithLocale(absValue / 1_000, locale, numberFormatOptions) + 'K'
    } else {
      shorthandValue = formatWithLocale(absValue, locale, numberFormatOptions)
    }

    return value < 0 ? '-' + shorthandValue : shorthandValue
  }

  return formatWithLocale(value, locale, numberFormatOptions)
}

export const numberFormatter = (
  value: unknown,
  options?: NumberFormatProps,
): string => {
  const {
    numberLocale = 'default',
    numberFormat = 'standard',
    numberRoundingMode,
    numberUnit = 'number',
    numberNullDisplay = '-',
  } = options || {}

  const isPercentage = numberUnit === 'percentage' || numberUnit === 'percent'
  const precision = options?.numberPrecision ?? (isPercentage ? 0 : 2)

  if (value === null || value === undefined) {
    return numberNullDisplay
  }

  const parsed = parseFiniteNumber(value)

  if (parsed === null) {
    return numberNullDisplay
  }

  const scaled =
    isPercentage && parsed.isRationalSlash ? parsed.value * 100 : parsed.value

  const body = formatDecimalDigits(scaled, {
    precision,
    locale: numberLocale,
    numberFormat:
      isPercentage && numberFormat === 'abbreviated'
        ? 'standard'
        : numberFormat,
    roundingMode: numberRoundingMode,
  })

  return isPercentage ? `${body}%` : body
}
